import type { Product, ProductFilters, ProductsResponse } from '~/types'

// Cache for product queries
interface CacheEntry {
  data: ProductsResponse
  timestamp: number
}

const queryCache = new Map<string, CacheEntry>()
const CACHE_TTL = 5 * 60 * 1000 // 5 minutes

export function useProducts() {
  const supabase = useSupabaseClient()

  // Generate cache key from filters and pagination
  const getCacheKey = (filters: ProductFilters, page: number, pageSize: number): string => {
    return JSON.stringify({ filters, page, pageSize })
  }

  // Check if cache entry is valid
  const isCacheValid = (entry: CacheEntry): boolean => {
    return Date.now() - entry.timestamp < CACHE_TTL
  }

  // Fetch products with filters and pagination
  const fetchProducts = async (
    filters: ProductFilters = {},
    page: number = 1,
    pageSize: number = 24
  ): Promise<ProductsResponse> => {
    // Check cache first
    const cacheKey = getCacheKey(filters, page, pageSize)
    const cached = queryCache.get(cacheKey)

    if (cached && isCacheValid(cached)) {
      return cached.data
    }

    try {
      let query = supabase
        .from('products')
        .select(`
          *,
          category:categories(*),
          product_line:product_lines(*)
        `, { count: 'exact' })
        .eq('is_active', true)

      // Apply filters
      if (filters.search) {
        const searchTerm = filters.search.trim()
        query = query.or(`name.ilike.%${searchTerm}%,stock_code.ilike.%${searchTerm}%`)
      }

      if (filters.categoryId) {
        query = query.eq('category_id', filters.categoryId)
      }

      if (filters.productLineId) {
        query = query.eq('product_line_id', filters.productLineId)
      }

      if (filters.componentType) {
        query = query.eq('component_type', filters.componentType)
      }

      if (filters.minPackSize !== undefined) {
        query = query.gte('pack_size', filters.minPackSize)
      }

      if (filters.maxPackSize !== undefined) {
        query = query.lte('pack_size', filters.maxPackSize)
      }

      // Pagination
      const from = (page - 1) * pageSize
      const to = from + pageSize - 1

      query = query
        .order('name')
        .range(from, to)

      const { data, error, count } = await query

      if (error) {
        console.error('Error fetching products:', error)
        throw error
      }

      const response: ProductsResponse = {
        products: (data as Product[]) || [],
        total: count || 0,
        page,
        pageSize,
        totalPages: Math.ceil((count || 0) / pageSize)
      }

      // Cache the response
      queryCache.set(cacheKey, {
        data: response,
        timestamp: Date.now()
      })

      return response
    } catch (error) {
      // Return empty response on error
      return {
        products: [],
        total: 0,
        page,
        pageSize,
        totalPages: 0
      }
    }
  }

  // Fetch single product by slug with error handling
  const fetchProductBySlug = async (slug: string): Promise<Product | null> => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select(`
          *,
          category:categories(*),
          product_line:product_lines(*)
        `)
        .eq('slug', slug)
        .eq('is_active', true)
        .single()

      if (error) {
        if (error.code === 'PGRST116') {
          return null // Not found
        }
        console.error('Error fetching product:', error)
        throw error
      }

      return data as Product
    } catch (error) {
      console.error('Error in fetchProductBySlug:', error)
      return null
    }
  }

  // Fetch related products (same category) with improved logic
  const fetchRelatedProducts = async (
    product: Product,
    limit: number = 4
  ): Promise<Product[]> => {
    if (!product.category_id) return []

    try {
      const { data, error } = await supabase
        .from('products')
        .select(`
          *,
          category:categories(*),
          product_line:product_lines(*)
        `)
        .eq('category_id', product.category_id)
        .eq('is_active', true)
        .neq('id', product.id)
        .order('name')
        .limit(limit)

      if (error) {
        console.error('Error fetching related products:', error)
        return []
      }

      return (data as Product[]) || []
    } catch (error) {
      console.error('Error in fetchRelatedProducts:', error)
      return []
    }
  }

  // Clear cache utility (useful for admin updates)
  const clearCache = () => {
    queryCache.clear()
  }

  return {
    fetchProducts,
    fetchProductBySlug,
    fetchRelatedProducts,
    clearCache
  }
}
